
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, CircleDollarSign, ToggleLeft, ToggleRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { RecurringTransaction } from "@/types/database";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";

export interface RecurringTransactionListProps {
  recurringTransactions: RecurringTransaction[];
  onRefresh: () => void;
}

export function RecurringTransactionList({ recurringTransactions, onRefresh }: RecurringTransactionListProps) {
  const [activeTab, setActiveTab] = useState<string>("all");
  
  // Toggle active status of recurring transaction
  const toggleActive = async (id: string, currentActiveStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('recurring_transactions')
        .update({ active: !currentActiveStatus })
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success(`Cobrança ${currentActiveStatus ? 'desativada' : 'ativada'} com sucesso!`);
      onRefresh();
    } catch (error: any) {
      console.error("Error toggling transaction status:", error);
      toast.error("Erro ao alterar status da cobrança recorrente");
    }
  };
  
  // Delete recurring transaction
  const deleteTransaction = async (id: string) => {
    try {
      const { error } = await supabase
        .from('recurring_transactions')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success("Cobrança recorrente excluída com sucesso!");
      onRefresh();
    } catch (error: any) {
      console.error("Error deleting transaction:", error);
      toast.error("Erro ao excluir cobrança recorrente");
    }
  };
  
  // Filter transactions based on active tab
  const filteredTransactions = recurringTransactions.filter(transaction => {
    if (activeTab === "all") return true;
    if (activeTab === "income") return transaction.transaction_type === "income";
    if (activeTab === "expense") return transaction.transaction_type === "expense";
    if (activeTab === "active") return transaction.active;
    if (activeTab === "inactive") return !transaction.active;
    return true;
  });
  
  const hasActiveTransactions = recurringTransactions.some(t => t.active);
  const hasPendingTransactions = recurringTransactions.some(t => t.days_until_due !== undefined && t.days_until_due <= 7);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Cobranças Recorrentes</CardTitle>
        <CardDescription>
          Visualize e gerencie suas cobranças e pagamentos mensais
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid grid-cols-2 md:grid-cols-5 mb-4">
            <TabsTrigger value="all">Todas</TabsTrigger>
            <TabsTrigger value="income">Receitas</TabsTrigger>
            <TabsTrigger value="expense">Despesas</TabsTrigger>
            <TabsTrigger value="active">Ativas</TabsTrigger>
            <TabsTrigger value="inactive">Inativas</TabsTrigger>
          </TabsList>
        </Tabs>
        
        {recurringTransactions.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-4">
              <Calendar className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-medium text-lg mb-2">Nenhuma cobrança recorrente</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-4">
              Configure cobranças recorrentes para automatizar a geração de transações mensais.
            </p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-muted-foreground">
              Nenhuma cobrança encontrada com o filtro selecionado.
            </p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Dia do Mês</TableHead>
                  <TableHead>Próximo Vencimento</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-start gap-2">
                        <Badge variant={transaction.transaction_type === "income" ? "default" : "destructive"}>
                          {transaction.transaction_type === "income" ? "Receita" : "Despesa"}
                        </Badge>
                        <span>{transaction.description}</span>
                      </div>
                      {transaction.category && (
                        <span className="text-xs text-muted-foreground block mt-1">
                          Categoria: {transaction.category}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{transaction.client_name || "N/A"}</TableCell>
                    <TableCell className={transaction.transaction_type === "income" ? "text-green-600" : "text-red-600"}>
                      R$ {transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>Dia {transaction.day_of_month}</TableCell>
                    <TableCell>
                      {transaction.next_due_date ? (
                        <div className="flex flex-col">
                          <span>{format(parseISO(transaction.next_due_date), "dd/MM/yyyy")}</span>
                          {transaction.days_until_due !== undefined && (
                            <Badge variant={transaction.days_until_due <= 3 ? "destructive" : "outline"} className="mt-1 w-fit">
                              {transaction.days_until_due === 0 
                                ? "Hoje" 
                                : transaction.days_until_due === 1 
                                  ? "Amanhã" 
                                  : `Em ${transaction.days_until_due} dias`}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={transaction.active ? "default" : "secondary"}>
                        {transaction.active ? "Ativa" : "Inativa"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => toggleActive(transaction.id, transaction.active)}
                        >
                          {transaction.active ? (
                            <ToggleRight className="h-4 w-4 mr-1" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 mr-1" />
                          )}
                          {transaction.active ? "Desativar" : "Ativar"}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => {
                            if (confirm("Tem certeza que deseja excluir esta cobrança recorrente?")) {
                              deleteTransaction(transaction.id);
                            }
                          }}
                        >
                          Excluir
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
